import type React from "react";
import { useEffect, useState } from "react";
import type { ChannelConnection, Product, WhatsAppTemplate } from "@qbanq/shared";
import { api, ApiError } from "../api/client";

export function ChannelsPage() {
  const [channels, setChannels] = useState<ChannelConnection[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [displayPhone, setDisplayPhone] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  // Per-connection transient UI state.
  const [healthMsg, setHealthMsg] = useState<Record<string, string>>({});
  const [testFor, setTestFor] = useState<string | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [testTo, setTestTo] = useState("");
  const [testTemplate, setTestTemplate] = useState("");
  const [testMsg, setTestMsg] = useState<string | null>(null);

  function load() {
    void api.get<ChannelConnection[]>("/api/channels").then(setChannels);
    void api.get<Product[]>("/api/products").then((p) => {
      setProducts(p);
      if (!productId && p[0]) setProductId(p[0].id);
    });
  }
  useEffect(load, []);

  async function connectWa(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setConnecting(true);
    try {
      await api.post("/api/channels/whatsapp/connect", {
        product_id: productId,
        phone_number_id: phoneNumberId,
        display_phone_number: displayPhone || undefined,
        access_token: accessToken,
      });
      setPhoneNumberId("");
      setDisplayPhone("");
      setAccessToken("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to connect");
    } finally {
      setConnecting(false);
    }
  }

  async function recheck(id: string) {
    setHealthMsg((m) => ({ ...m, [id]: "checking…" }));
    try {
      const h = await api.get<{ ok: boolean; error?: string; verified_name?: string }>(
        `/api/channels/${id}/health`,
      );
      setHealthMsg((m) => ({
        ...m,
        [id]: h.ok ? `✓ ${h.verified_name ?? "verified"}` : `✗ ${h.error ?? "failed"}`,
      }));
      load();
    } catch (err) {
      setHealthMsg((m) => ({ ...m, [id]: err instanceof Error ? err.message : "error" }));
    }
  }

  async function openTest(conn: ChannelConnection) {
    setTestFor(conn.id);
    setTestMsg(null);
    setTestTo("");
    const t = await api.get<WhatsAppTemplate[]>(`/api/whatsapp-templates?product_id=${conn.product_id}`);
    setTemplates(t);
    setTestTemplate(t[0]?.id ?? "");
  }

  async function sendTest(id: string) {
    setTestMsg("sending…");
    try {
      const r = await api.post<{ provider_message_id: string }>(`/api/channels/${id}/test`, {
        to: testTo,
        template_id: testTemplate,
      });
      setTestMsg(`✓ sent (id ${r.provider_message_id || "—"})`);
    } catch (err) {
      setTestMsg(err instanceof ApiError ? `✗ ${err.message}` : "✗ failed");
    }
  }

  async function disconnect(id: string) {
    await api.post(`/api/channels/${id}/disconnect`);
    load();
  }

  return (
    <div>
      <h2>Channel Connections</h2>
      <p className="muted">
        Connect a WhatsApp number to a product. The number's access token is validated against
        the WhatsApp Cloud API and stored <strong>encrypted</strong> — it never leaves the server.
        Once connected, the support bot replies automatically to that number.
      </p>

      <div className="card">
        <h3>Connect WhatsApp number</h3>
        <form onSubmit={connectWa}>
          <div className="field">
            <label>Product</label>
            <select value={productId} onChange={(e) => setProductId(e.target.value)}>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label>Phone number ID</label>
              <input value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)} placeholder="from Meta → WhatsApp → API setup" />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Display phone number (optional)</label>
              <input value={displayPhone} onChange={(e) => setDisplayPhone(e.target.value)} placeholder="+1 555…" />
            </div>
          </div>
          <div className="field">
            <label>Access token</label>
            <input type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder="WhatsApp Cloud API access token" />
          </div>
          {error && <p className="error">{error}</p>}
          <button className="btn" type="submit" disabled={connecting}>
            {connecting ? "Validating…" : "Connect & validate"}
          </button>
        </form>
      </div>

      <div className="card">
        <table>
          <thead>
            <tr><th>Channel</th><th>Number / ID</th><th>Product</th><th>Health</th><th></th></tr>
          </thead>
          <tbody>
            {channels.map((c) => (
              <tr key={c.id}>
                <td>{c.channel}</td>
                <td>
                  {c.verified_name ?? c.display_name ?? "—"}
                  <div className="muted" style={{ fontSize: 12 }}>{c.external_id}</div>
                </td>
                <td>{products.find((p) => p.id === c.product_id)?.name ?? c.product_id}</td>
                <td>
                  <span className={`badge ${c.status === "connected" ? "ai" : ""}`}>{c.status}</span>
                  {c.quality_rating && <span className="muted" style={{ fontSize: 12 }}> · {c.quality_rating}</span>}
                  {healthMsg[c.id] && <div className="muted" style={{ fontSize: 12 }}>{healthMsg[c.id]}</div>}
                </td>
                <td>
                  <div className="row">
                    {c.channel === "whatsapp" && c.status === "connected" && (
                      <>
                        <button className="btn secondary" onClick={() => recheck(c.id)}>Re-check</button>
                        <button className="btn secondary" onClick={() => openTest(c)}>Test</button>
                      </>
                    )}
                    {c.status === "connected" && (
                      <button className="btn danger" onClick={() => disconnect(c.id)}>Disconnect</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {channels.length === 0 && (
              <tr><td colSpan={5} className="muted">No channels connected.</td></tr>
            )}
          </tbody>
        </table>

        {testFor && (
          <div className="card" style={{ background: "var(--panel-2)", marginTop: 12 }}>
            <strong>Send a test message</strong>
            <p className="muted" style={{ fontSize: 13 }}>
              Sends an approved template to a recipient (free-form text isn't allowed outside the
              24h window). Register templates on the WhatsApp Templates page first.
            </p>
            {templates.length === 0 ? (
              <p className="error">No templates registered for this product.</p>
            ) : (
              <div className="row">
                <div className="field" style={{ flex: 1 }}>
                  <label>Recipient number (E.164)</label>
                  <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="15551234567" />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>Template</label>
                  <select value={testTemplate} onChange={(e) => setTestTemplate(e.target.value)}>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div className="row">
              <button className="btn" disabled={!testTo || !testTemplate} onClick={() => sendTest(testFor)}>Send test</button>
              <button className="btn secondary" onClick={() => { setTestFor(null); setTestMsg(null); }}>Close</button>
              {testMsg && <span className="muted">{testMsg}</span>}
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="muted">Facebook Messenger (Phase 2)</h3>
        <p className="muted">Connect endpoint exists (<code>/api/channels/messenger/connect</code>); wire when ready.</p>
      </div>
    </div>
  );
}
